
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ExternalLink, Star, Eye } from 'lucide-react';
import { Resource } from '@/services/resourceService';

interface ResourceCardProps {
  resource: Resource;
  onRate?: (id: string, rating: number) => void;
}

const ResourceCard: React.FC<ResourceCardProps> = ({
  resource,
  onRate
}) => {
  const getDifficultyColor = (level?: string) => {
    switch (level) {
      case 'beginner': return 'default';
      case 'intermediate': return 'secondary';
      case 'advanced': return 'destructive';
      default: return 'outline';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'guide': return '📚';
      case 'tool': return '🔧';
      case 'template': return '📋';
      case 'video': return '🎥';
      case 'article': return '📄';
      default: return '📄';
    }
  };

  return (
    <Card className="h-full flex flex-col hover:shadow-lg transition-shadow">
      <CardHeader className="pb-3 flex-shrink-0">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <span className="text-lg">{getTypeIcon(resource.type)}</span>
          <Badge variant="outline" className="text-xs">
            {resource.type}
          </Badge>
          {resource.difficulty_level && (
            <Badge variant={getDifficultyColor(resource.difficulty_level)} className="text-xs">
              {resource.difficulty_level}
            </Badge>
          )}
          {resource.is_featured && (
            <Badge variant="default" className="text-xs">
              Featured
            </Badge>
          )}
        </div>
        
        <CardTitle className="text-lg leading-tight">
          <div className="line-clamp-2">
            {resource.title}
          </div>
        </CardTitle>
        
        {resource.provider && (
          <p className="text-sm text-muted-foreground truncate">by {resource.provider}</p>
        )}
      </CardHeader>
      
      <CardContent className="pt-0 flex-1 flex flex-col">
        <CardDescription className="flex-1 mb-4">
          <div className="line-clamp-3">
            {resource.description}
          </div>
        </CardDescription>
        
        <div className="flex items-center gap-4 mb-4 text-xs text-muted-foreground">
          {resource.rating_count > 0 && (
            <div className="flex items-center gap-1">
              <Star className="h-3 w-3 fill-current text-yellow-500" />
              <span>{resource.rating_avg.toFixed(1)}</span>
              <span>({resource.rating_count})</span>
            </div>
          )}
          <div className="flex items-center gap-1">
            <Eye className="h-3 w-3" />
            <span>{resource.view_count}</span>
          </div>
        </div>
        
        {resource.tags && resource.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-4">
            {resource.tags.slice(0, 3).map((tag) => (
              <Badge key={tag} variant="outline" className="text-xs">
                {tag}
              </Badge>
            ))}
            {resource.tags.length > 3 && (
              <Badge variant="outline" className="text-xs">
                +{resource.tags.length - 3}
              </Badge>
            )}
          </div>
        )}
        
        <div className="mt-auto">
          <Button 
            className="w-full" 
            onClick={() => window.open(resource.url, '_blank')}
          >
            <ExternalLink className="h-4 w-4 mr-2" />
            View Resource
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default ResourceCard;
